import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PenTool, Pencil, Ruler, Trash2 } from 'lucide-react';
import type { CapturedMeasurementsInput } from '@tailoring-crm/shared';
import { useTemplates } from '../../lib/measurements/use-templates.js';
import { cn, Badge, Button } from '@tailoring-crm/ui';
import { MeasurementCaptureDialog } from './measurement-capture-dialog.js';

/**
 * Measurements taken while adding a customer who does not exist yet.
 *
 * A tape is in somebody's hand and the person it is round is standing at the counter. Until now
 * the shop had to save her first and measure her second, because a profile is owned by a
 * customer and there was no customer to own one. These are held here instead and posted WITH
 * her — see `createCustomerWithMeasurements` in the API, which writes both in one transaction
 * so there is never a customer with her measurements mislaid.
 *
 * NOT `MeasurementsPanel`, deliberately. That panel reads the server, and every affordance on it
 * — history, duplicate, the staleness badge, delete — is about a record that exists. None of
 * those questions can be asked of a reading that has not been saved yet, and a panel that
 * offered them would be four buttons that do nothing. What these share is the capture dialog,
 * which is the part that must not diverge: the same template, the same fields, the same parser.
 */

export interface MeasurementDraft {
  /** Local only, for React keys and for naming which one the counter is correcting. */
  key: string;
  captured: CapturedMeasurementsInput;
}

export interface DraftMeasurementsPanelProps {
  drafts: readonly MeasurementDraft[];
  onChange: (drafts: MeasurementDraft[]) => void;
  className?: string;
}

/** Unique within one form's lifetime, which is all these have to be. */
function nextKey(): string {
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function DraftMeasurementsPanel({
  drafts,
  onChange,
  className,
}: DraftMeasurementsPanelProps): ReactElement {
  const { t } = useTranslation();
  const templates = useTemplates();

  /** The garment chosen for a NEW capture, or the draft being corrected. Never both. */
  const [capturing, setCapturing] = useState<string | null>(null);
  const [editing, setEditing] = useState<MeasurementDraft | null>(null);

  const allTemplates = templates.data?.templates ?? [];
  const labelFor = (garmentTypeKey: string): string =>
    allTemplates.find((one) => one.garmentTypeKey === garmentTypeKey)?.garmentTypeLabel ??
    garmentTypeKey;

  const add = (captured: CapturedMeasurementsInput): void => {
    onChange([...drafts, { key: nextKey(), captured }]);
  };

  const replace = (key: string, captured: CapturedMeasurementsInput): void => {
    onChange(drafts.map((draft) => (draft.key === key ? { ...draft, captured } : draft)));
  };

  const remove = (key: string): void => {
    onChange(drafts.filter((draft) => draft.key !== key));
  };

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Ruler aria-hidden="true" className="size-4" />
          {t('profiles.heading')}
        </h3>
        {/* The garment picker IS the add button, for the same reason it is on the saved panel:
            choosing what you are measuring and starting to measure are one action at a counter. */}
        <select
          aria-label={t('profiles.chooseGarment')}
          value=""
          onChange={(event) => event.target.value && setCapturing(event.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">{t('profiles.add')}</option>
          {allTemplates.map((template) => (
            <option key={template.garmentTypeKey} value={template.garmentTypeKey}>
              {t('profiles.addFor', { garment: template.garmentTypeLabel })}
            </option>
          ))}
        </select>
      </div>

      {drafts.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          {t('profiles.draftEmpty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {drafts.map((draft) => (
            <li
              key={draft.key}
              className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-border p-3"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium">
                  {draft.captured.name}
                  <Badge variant="secondary">{labelFor(draft.captured.garmentTypeKey)}</Badge>
                  {/* Said plainly, not implied. Somebody who walks away from this form without
                      saving has to know these went with it. */}
                  <Badge variant="warning">{t('profiles.draftPending')}</Badge>
                </p>
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  {t('profiles.valueCount', { count: draft.captured.values?.length ?? 0 })}
                  {draft.captured.sketchId ? (
                    <span className="inline-flex items-center gap-1">
                      <PenTool aria-hidden="true" className="size-3.5" />
                      {t('measurementSketch.badge')}
                    </span>
                  ) : null}
                </p>
                {draft.captured.notes ? (
                  <p className="text-sm text-muted-foreground">{draft.captured.notes}</p>
                ) : null}
              </div>

              <div className="flex shrink-0 gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('profiles.edit')}
                  onClick={() => setEditing(draft)}
                >
                  <Pencil aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('profiles.draftRemove')}
                  onClick={() => remove(draft.key)}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-sm text-muted-foreground">{t('profiles.draftHint')}</p>

      {capturing ? (
        <MeasurementCaptureDialog
          open
          onOpenChange={(next) => !next && setCapturing(null)}
          garmentTypeKey={capturing}
          onCaptured={add}
        />
      ) : null}

      {editing ? (
        <MeasurementCaptureDialog
          open
          onOpenChange={(next) => !next && setEditing(null)}
          garmentTypeKey={editing.captured.garmentTypeKey}
          draft={editing.captured}
          onCaptured={(captured) => replace(editing.key, captured)}
        />
      ) : null}
    </section>
  );
}
